import { Link } from "react-router-dom";
import {
  ArrowRight, Building2, KeyRound, Hammer, Landmark, ShieldCheck, MapPin,
  ClipboardCheck, AlertTriangle, Users2, LineChart, FileSearch,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/lib/seo";
import {
  Reveal, SectionHeading, CTASection, ComplianceNote, CheckItem, FAQSection, ProcessTimeline, stagger, fadeUp,
} from "@/components/shared/Shared";
import { motion } from "framer-motion";

const IMG_HERO = "https://images.unsplash.com/photo-1460317442991-0ec209397118?q=80&w=1600&auto=format&fit=crop";

export default function RealEstate() {
  const kriterien = [
    { title: "Stabiler Arbeitsmarkt", text: "Standorte mit breiter Arbeitgeberstruktur statt Abhängigkeit von einem einzelnen Unternehmen." },
    { title: "Bevölkerungsprognose", text: "Nur Regionen mit positiver Bevölkerungsentwicklung in den offiziellen Prognosen bis 2040." },
    { title: "Miete unter Marktniveau", text: "Bestandsmieten unterhalb des örtlichen Niveaus – als Substanzmerkmal, nicht als Versprechen." },
    { title: "Geprüfte Bausubstanz", text: "Technische Prüfung des Objekts – bei Bedarf inklusive Gutachten zur Restnutzungsdauer." },
    { title: "Rücklage der Eigentümergemeinschaft", text: "Ausreichende Instandhaltungsrücklage und geordnete Verwaltungsunterlagen der WEG." },
    { title: "Wiederverkäuflichkeit", text: "Gefragte Wohnungsgrößen und Lagen mit niedriger Leerstandsquote – damit das Objekt auch morgen gefragt bleibt." },
  ];

  const ablauf = [
    { title: "Finanzierungscheck", text: "Kostenfreies Erstgespräch mit kurzer Selbstauskunft – als Grundlage für Ihren realistischen Rahmen." },
    { title: "Finanzierungsrahmen", text: "Prüfung über unsere Bankpartner – Rückmeldung zu Ihrem Rahmen in der Regel innerhalb von 48 Stunden." },
    { title: "Objektvorschläge", text: "Zwei bis drei passende, vorgeprüfte Wohnungen – mit vollständigen Objektunterlagen zum Nachlesen." },
    { title: "Prüfung & Notartermin", text: "Sie prüfen in Ruhe, gern mit Ihrem Steuerberater. Danach begleiten wir Kaufvertrag und Notartermin." },
    { title: "Vermietung & Betreuung", text: "Sanierung, Neuvermietung und Übergabe an die Hausverwaltung – ein Ansprechpartner über die gesamte Haltedauer." },
  ];

  const risiken = [
    { title: "Mietausfall & Leerstand", text: "Mieter können ausfallen, Wohnungen vorübergehend leer stehen. Sorgfältige Mieterauswahl und optionale Mietausfallversicherung mindern das Risiko – ausschließen lässt es sich nicht." },
    { title: "Instandhaltung", text: "Als Eigentümer tragen Sie laufende Kosten für Verwaltung und Instandhaltung. Diese werden in der Kalkulation von Beginn an berücksichtigt." },
    { title: "Zinsänderung", text: "Nach Ablauf der Zinsbindung (üblich sind 10–15 Jahre) kann die Anschlussfinanzierung teurer werden. Das Zinsniveau der Zukunft ist nicht vorhersehbar." },
    { title: "Wertentwicklung", text: "Immobilienwerte können steigen und fallen. Eine Wertsteigerung ist möglich, aber nicht garantiert – wir kalkulieren bewusst ohne sie." },
    { title: "Langfristige Bindung", text: "Eine Immobilie ist keine kurzfristig liquidierbare Anlage. Sie eignet sich für Anleger mit langem Horizont und stabiler persönlicher Situation." },
    { title: "Persönliche Situation", text: "Einkommen und Lebensumstände können sich ändern. Deshalb prüfen wir vor jedem Kauf, ob die Finanzierung auch konservativ gerechnet tragfähig ist." },
  ];

  const faq = [
    {
      q: "Brauche ich viel Eigenkapital?",
      a: "Das hängt von Bonität und Objekt ab. Je nach Situation ist eine Finanzierung von bis zu 110 % des Kaufpreises möglich – also inklusive Nebenkosten. Ob und in welcher Höhe das für Sie realistisch ist, klärt der kostenfreie Finanzierungscheck.",
    },
    {
      q: "Wer kümmert sich um Mieter und Verwaltung?",
      a: "Die laufende Betreuung übernimmt die Hausverwaltung der Gruppe – mit über 400 Wohnungen in eigener Verwaltung. Sie haben einen festen Ansprechpartner über die gesamte Haltedauer.",
    },
    {
      q: "Zahlt der Mieter meine Wohnung ab?",
      a: "Die Mieteinnahmen tragen einen wesentlichen Teil der Finanzierungsrate – wie viel genau, hängt von Objekt, Finanzierung und Ihrer Situation ab. Eine pauschale Aussage wäre unseriös; Ihre individuelle Kalkulation erhalten Sie im Gespräch, klar als Modellrechnung gekennzeichnet.",
    },
    {
      q: "Welche steuerlichen Vorteile habe ich?",
      a: "Bei vermieteten Immobilien können sich steuerliche Effekte ergeben – etwa über Abschreibungen. Ob und in welcher Höhe, hängt von Ihrer persönlichen Situation ab und wird mit Ihrem Steuerberater geklärt. Eleven erbringt keine Steuerberatung.",
    },
    {
      q: "Kann ich das Objekt vorher prüfen und besichtigen?",
      a: "Ja. Sie erhalten zu jedem Vorschlag vollständige Objektunterlagen – inklusive Protokollen, Rücklagen und Mietvertrag. Eine Besichtigung ist selbstverständlich möglich, und Sie entscheiden ohne Zeitdruck.",
    },
    {
      q: "Was passiert, wenn ich wieder verkaufen möchte?",
      a: "Bestandswohnungen in gefragten Lagen sind grundsätzlich wieder verkäuflich – die Wiederverkäuflichkeit ist eines unserer Ankaufskriterien. Dennoch ist die Kapitalanlage langfristig gedacht; ein kurzfristiger Verkauf kann mit Verlusten verbunden sein.",
    },
  ];

  return (
    <div className="bg-white">
      <Seo
        title="Eleven Real Estate – Vermögensaufbau mit vermieteten Bestandswohnungen"
        description="Geprüfte, sanierte Bestandswohnungen als langfristige Kapitalanlage: Objekt- und Standortprüfung, Finanzierung über Bankpartner, Notartermin und laufende Betreuung – aus einer Hand."
      />

      {/* HERO */}
      <section className="bg-[#111318] noise-overlay accent-hairline" data-testid="re-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div variants={stagger} initial="hidden" animate="show" className="lg:col-span-7">
              <motion.div variants={fadeUp} className="flex items-center gap-3">
                <span className="h-px w-8 bg-[#FF3C20]" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60">Eleven Real Estate</p>
              </motion.div>
              <motion.h1 variants={fadeUp} className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] text-white" data-testid="re-hero-headline">
                Vermögensaufbau mit vermieteten Bestandswohnungen.
              </motion.h1>
              <motion.p variants={fadeUp} className="mt-5 text-base md:text-lg text-white/60 leading-relaxed max-w-[60ch]">
                Geprüfte, sanierte 2- bis 3-Zimmer-Wohnungen in wirtschaftsstarken Regionen – vom Finanzierungscheck über den Notartermin bis zur laufenden Verwaltung. Ein Ansprechpartner über die gesamte Haltedauer.
              </motion.p>
              <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-[6px] h-12 px-6 bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold" data-testid="re-hero-primary-cta">
                  <Link to="/anfrage/immobilien">Kostenfreier Finanzierungscheck <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-[6px] h-12 px-6 border-white/25 text-white bg-transparent hover:bg-white/5 hover:text-white hover:border-white" data-testid="re-hero-secondary-cta">
                  <Link to="/termin">Erstgespräch vereinbaren</Link>
                </Button>
              </motion.div>
              <motion.div variants={fadeUp} className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-y-2.5 gap-x-6 max-w-2xl">
                <CheckItem dark>Nur geprüfte Bestandsobjekte</CheckItem>
                <CheckItem dark>Finanzierung bis 110 % möglich*</CheckItem>
                <CheckItem dark>Verwaltung aus einer Hand</CheckItem>
              </motion.div>
              <motion.p variants={fadeUp} className="mt-3 text-[11px] text-white/35">*abhängig von Bonität und Objekt – Prüfung über mehrere Bankpartner</motion.p>
            </motion.div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative rounded-[8px] overflow-hidden ring-1 ring-white/10 ">
                <img src={IMG_HERO} alt="Bestandswohnungen als Kapitalanlage – Eleven Real Estate" className="w-full h-[440px] object-cover" loading="eager" decoding="async" data-testid="re-hero-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111318]/60 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WIE ES FUNKTIONIERT */}
      <section className="bg-white py-14 sm:py-20" data-testid="re-funktionsweise">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Das Prinzip"
            title="Wie eine fremdfinanzierte Bestandswohnung funktioniert"
            subtitle="Drei Beteiligte, eine klare Rollenverteilung – verständlich erklärt, ohne Rechenversprechen."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Landmark, title: "Die Bank finanziert", text: "Der Kaufpreis wird überwiegend über eine Bankfinanzierung getragen – je nach Bonität und Objekt sind bis zu 110 % möglich. Die Zinsbindung liegt üblicherweise bei 10 bis 15 Jahren." },
              { icon: KeyRound, title: "Der Mieter zahlt Miete", text: "Die monatlichen Mieteinnahmen tragen einen wesentlichen Teil der Finanzierungsrate. Wie viel Sie selbst beisteuern, hängt von Objekt, Zins und Ihrer Situation ab." },
              { icon: LineChart, title: "Sie bauen Vermögen auf", text: "Mit jeder Rate sinkt die Restschuld – Monat für Monat entsteht Eigentum. Mögliche steuerliche Effekte klären Sie individuell mit Ihrem Steuerberater." },
            ].map((c, i) => (
              <Reveal key={c.title} className="rounded-[8px] border border-border bg-[#F6F7F8] p-8" data-testid={`re-prinzip-${i + 1}`}>
                <div className="h-11 w-11 rounded-[6px] bg-[#111318] flex items-center justify-center">
                  <c.icon className="h-5 w-5 text-[#FF3C20]" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold text-[#111318]">{c.title}</h3>
                <p className="mt-2.5 text-sm text-[#3B4452] leading-relaxed">{c.text}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 max-w-4xl">
            <ComplianceNote>
              Ihre individuelle Kalkulation – inklusive Rate, Mieteinnahmen und laufenden Kosten – erhalten Sie im persönlichen Gespräch, klar als unverbindliche Modellrechnung gekennzeichnet. Pauschale Aussagen wie „der Mieter zahlt die Wohnung komplett ab“ oder automatische Wertsteigerungen wären unseriös – darauf verzichten wir bewusst.
            </ComplianceNote>
          </div>
        </div>
      </section>

      {/* ANKAUFSKRITERIEN */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20" data-testid="re-kriterien">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Ankaufskriterien"
            title="Sechs Kriterien. Kein Objekt ohne Prüfung."
            subtitle="Wir kaufen keine Projekte vom Plan, sondern vermietete Bestandswohnungen – typischerweise 2 bis 3 Zimmer mit 55 bis 75 m², saniert vor Neuvermietung."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {kriterien.map((k, i) => (
              <Reveal key={k.title} className="rounded-[8px] bg-white border border-border p-7 card-hover" data-testid={`re-kriterium-${i + 1}`}>
                <span className="font-mono-num text-xs font-semibold text-[#E8241B]">0{i + 1}</span>
                <h3 className="mt-3 text-base font-semibold text-[#111318]">{k.title}</h3>
                <p className="mt-2 text-sm text-[#3B4452] leading-relaxed">{k.text}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 rounded-[8px] bg-[#111318] noise-overlay p-8 sm:p-9">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="h-12 w-12 rounded-[6px] bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-[#FF3C20]" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg font-semibold text-white">Bewusst nur vier Bundesländer</h3>
                <p className="mt-1.5 text-sm text-white/55 leading-relaxed max-w-3xl">
                  Wir konzentrieren uns auf wirtschaftsstarke Regionen in Baden-Württemberg, Bayern, Hessen und Nordrhein-Westfalen – dort, wo Arbeitsmarkt, Bevölkerungsentwicklung und Nachfrage unsere Kriterien erfüllen.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {["Baden-Württemberg", "Bayern", "Hessen", "NRW"].map((b) => (
                  <Badge key={b} variant="outline" className="rounded-[6px] border-[#FF3C20]/40 bg-[#FF3C20]/10 text-[#FF3C20]">{b}</Badge>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SANIERUNG & BETREUUNG */}
      <section className="bg-white py-14 sm:py-20" data-testid="re-sanierung">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <SectionHeading
                eyebrow="Sanierung & Vermietbarkeit"
                title="Saniert, bevor der Mieter einzieht"
                subtitle="Vor der Neuvermietung wird jede Wohnung nach festen Standards hergerichtet – durch Eleven Development und unseren Bauträger- und Sanierungspartner Relago."
              />
              <div className="mt-7 space-y-3">
                <CheckItem>Feste Ausstattungsstandards – von Bädern über Böden bis zur Elektrik</CheckItem>
                <CheckItem>Fachbetrieb statt Preisliste: Qualität und Termine bleiben im Haus</CheckItem>
                <CheckItem>Herrichtung vor Neuvermietung – für nachhaltige Vermietbarkeit</CheckItem>
              </div>
              <Button asChild variant="outline" className="mt-7 rounded-[6px] h-11 px-5 hover:border-white" data-testid="re-development-link">
                <Link to="/development">Mehr zu Eleven Development <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
            </div>
            <div>
              <SectionHeading
                eyebrow="Nach dem Kauf"
                title="Betreuung über die gesamte Haltedauer"
                subtitle="Mit dem Notartermin endet unsere Arbeit nicht – sie beginnt dort erst richtig."
              />
              <div className="mt-7 space-y-5">
                {[
                  { icon: Users2, title: "Hausverwaltung der Gruppe", text: "Über 400 Wohnungen in eigener Verwaltung – Mieterkommunikation, Abrechnung und Instandhaltung aus einer Hand." },
                  { icon: ShieldCheck, title: "Absicherung möglich", text: "Auf Wunsch kann eine Mietausfallversicherung eingebunden werden – Details und Kosten transparent im Gespräch." },
                  { icon: ClipboardCheck, title: "Vollständige Unterlagen", text: "Sie erhalten zu jedem Objekt vollständige Unterlagen – vom WEG-Protokoll bis zum Mietvertrag – vor dem Notartermin." },
                ].map((p) => (
                  <div key={p.title} className="flex gap-4">
                    <div className="h-10 w-10 rounded-[6px] bg-[#F1F2F4] border border-border flex items-center justify-center shrink-0">
                      <p.icon className="h-4.5 w-4.5 h-5 w-5 text-[#E8241B]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#111318]">{p.title}</p>
                      <p className="mt-1 text-sm text-[#3B4452] leading-relaxed">{p.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RISIKEN */}
      <section className="dark-section noise-overlay py-14 sm:py-20" data-testid="re-risiken">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            dark
            eyebrow="Risiken & laufende Kosten"
            title="Eine Immobilie ist eine unternehmerische Entscheidung"
            subtitle="Wer Ihnen nur Chancen zeigt, berät Sie nicht – er verkauft. Diese Risiken gehören zu jeder ehrlichen Betrachtung."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {risiken.map((r, i) => (
              <Reveal key={r.title} className="panel panel-hover p-7" data-testid={`re-risiko-${i + 1}`}>
                <AlertTriangle className="h-4 w-4 text-[#FF3C20]" />
                <h3 className="mt-3.5 text-base font-semibold text-white">{r.title}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{r.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABLAUF */}
      <section className="bg-white py-14 sm:py-20" data-testid="re-ablauf">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Ablauf"
            title="Vom Erstgespräch zum Eigentum – in der Regel 8 bis 10 Wochen"
            subtitle="Strukturiert, dokumentiert und ohne Zeitdruck – Sie entscheiden bei jedem Schritt."
          />
          <div className="mt-12">
            <ProcessTimeline steps={ablauf} />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20" data-testid="re-faq">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Häufige Fragen" title="Fragen zur Kapitalanlage-Immobilie" />
          <div className="mt-10">
            <FAQSection items={faq} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Finden Sie heraus, was in Ihrem Rahmen möglich ist."
        subtitle="Der kostenfreie Finanzierungscheck zeigt Ihnen unverbindlich, welcher Investitionsrahmen zu Ihrer Situation passt – Rückmeldung in der Regel innerhalb von 48 Stunden."
        primaryLabel="Kostenfreier Finanzierungscheck"
        primaryTo="/anfrage/immobilien"
        secondaryLabel="Erstgespräch vereinbaren"
        secondaryTo="/termin"
      />
    </div>
  );
}
